import React, { useEffect, useState } from "react";
import LoggedOutPage from "./LoggedOutPage";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { useFetch } from "./FetchProvider";
import { useParams } from "react-router-dom";

type StageName = "processing" | "email_is_verified" | "token_not_found" | "other_error";

type Stage = {
    stage: StageName;
    values: Record<string, string> | null;
};

type StageTranslations = {
    [key in StageName]: MessageDescriptor;
}

const stageTranslations: StageTranslations = {
    processing: {
        id: "email_verification.stage.processing",
        defaultMessage: "Processing...",
    },
    email_is_verified: {
        id: "email_verification.stage.email_is_verified",
        defaultMessage: "Email {address} is verified",
    },
    token_not_found: {
        id: "email_verification.stage.token_not_found",
        defaultMessage: "Email already verified",
    },
    other_error: {
        id: "email_verification.stage.other_error",
        defaultMessage: "Email validation failed. Please try again later.",
    },
} as const;

const EmailVerification = () => {
    const params = useParams<{ token: string }>();
    const fetch = useFetch();
    const [stage, setStage] = useState<Stage>({ stage: "processing", values: null });

    useEffect(() => {
        async function _fetchData() {
            const resp = await fetch("/public/verify-email", {
                method: "POST",
                body: JSON.stringify({
                    token: params.token,
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });
            if (resp.ok) {
                setStage({ stage: "email_is_verified", values: { address: (await resp.json()).address } });
            } else if (resp.status === 404) {
                setStage({ stage: "token_not_found", values: null });
            } else {
                setStage({ stage: "other_error", values: null });
            }
        }

        _fetchData();
    }, [fetch, params.token]);

    return (
        <LoggedOutPage title={<FormattedMessage id="email_verification.title" defaultMessage="Email verification" />}>
            <div style={{ textAlign: "center", fontSize: "1.5em" }}>
                <p>
                    <FormattedMessage {...stageTranslations[stage.stage]} values={stage.values} />
                </p>
                {["email_is_verified", "token_not_found", "other_error"].includes(stage.stage) ? (
                    <p>
                        <a href="/public/login">
                            <FormattedMessage id="email_verification.go_to_login" defaultMessage="Go to login page" values={stage.values} />
                        </a>
                    </p>
                ) : null}
            </div>
        </LoggedOutPage>
    );
};

export default EmailVerification;
